// workflowStepChecklistUtils.js
// Task Checklist recipe on workflow people steps + seed / completeness helpers.

import {
  getChecklistTemplateItemAccomplishmentPhrase,
  getChecklistTemplateItemLabel,
  normalizeChecklistTemplateItems,
} from 'utilities/ChecklistTemplateItems';
import { computeChecklistProgress } from 'services/checklists/checklistModels';

/**
 * resolveTaskChecklistFieldName
 *
 * Prefers `checklist`, else the first checklist field on the TASK type.
 *
 * @param {object|null} typeDef
 * @returns {string}
 */
export function resolveTaskChecklistFieldName(typeDef = null) {
  const fields = Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : [];
  const checklistFields = fields.filter((field) => (
    String(field?.type || '').trim().toLowerCase() === 'checklist'
  ));

  const preferred = checklistFields.find((field) => (
    String(field?.name || '').trim().toLowerCase() === 'checklist'
  ));
  if (preferred) return String(preferred.name).trim();

  return String(checklistFields[0]?.name || '').trim() || 'checklist';
}

/**
 * Normalize Task Checklist items authored on a workflow people step.
 *
 * @param {unknown} items
 * @returns {Array<object>}
 */
export function normalizeWorkflowTaskChecklistItems(items) {
  return normalizeChecklistTemplateItems(items);
}

/**
 * @param {object|null|undefined} task
 * @param {object|null|undefined} [templateTask] - Current template step; preferred for
 *   authored recipe metadata (labels, actions) when the run snapshot is stale.
 * @returns {{ items: Array<object>, required: boolean }}
 */
export function readWorkflowTaskChecklistRecipe(task = null, templateTask = null) {
  const templateItems = normalizeWorkflowTaskChecklistItems(templateTask?.taskChecklistItems);
  const runItems = normalizeWorkflowTaskChecklistItems(task?.taskChecklistItems);

  // Prefer template recipe when it has items (includes checklist item actions).
  // Fall back to the run task for older launches / missing template match.
  const items = templateItems.length > 0 ? templateItems : runItems;
  const required = templateTask != null
    ? templateTask?.taskChecklistRequired === true
    : task?.taskChecklistRequired === true;

  return { items, required };
}

/**
 * Build checklist snapshot seed items from a Task Checklist recipe.
 *
 * @param {unknown} recipeItems
 * @returns {Array<object>}
 */
export function buildWorkflowTaskChecklistSeedItems(recipeItems) {
  return normalizeWorkflowTaskChecklistItems(recipeItems).map((item, index) => {
    const accomplishmentPhrase = getChecklistTemplateItemAccomplishmentPhrase(item);
    return {
      key: item.key,
      text: getChecklistTemplateItemLabel(item),
      position: index,
      status: 'todo',
      source: 'template',
      templateKey: item.key,
      ...(accomplishmentPhrase ? { accomplishmentPhrase } : {}),
    };
  });
}

/**
 * Recipe-item progress for a Task Checklist (customs do not count when recipe exists).
 *
 * @param {unknown} recipeItems
 * @param {unknown} snapshotItems
 * @returns {{ totalItems: number, completedItems: number, isComplete: boolean }}
 */
export function computeWorkflowTaskChecklistProgress(recipeItems, snapshotItems) {
  const recipe = normalizeWorkflowTaskChecklistItems(recipeItems);
  const templateKeys = recipe.map((item) => String(item.key || '').trim()).filter(Boolean);

  if (!templateKeys.length) {
    const freeform = computeChecklistProgress(snapshotItems);
    return {
      totalItems: Number(freeform?.totalItems) || 0,
      completedItems: Number(freeform?.completedItems) || 0,
      isComplete: Number(freeform?.totalItems) > 0
        && Number(freeform?.completedItems) >= Number(freeform?.totalItems),
    };
  }

  const progress = computeChecklistProgress(snapshotItems, { templateKeys });
  const totalItems = Number(progress?.totalItems) || templateKeys.length;
  const completedItems = Number(progress?.completedItems) || 0;

  return {
    totalItems,
    completedItems,
    isComplete: totalItems > 0 && completedItems >= totalItems,
  };
}

/**
 * Compact percent label for a Task Checklist, e.g. "33%".
 *
 * @param {{ totalItems?: number, completedItems?: number }|null|undefined} progress
 * @returns {string}
 */
export function formatWorkflowTaskChecklistPercent(progress) {
  const total = Number(progress?.totalItems);
  const completed = Number(progress?.completedItems);
  if (!Number.isFinite(total) || total <= 0) return '';
  const done = Number.isFinite(completed)
    ? Math.min(Math.max(0, completed), total)
    : 0;
  return `${Math.round((done / total) * 100)}%`;
}

/**
 * Whether Required Task Checklist blocks Complete for this run step.
 *
 * @param {object|null|undefined} task
 * @param {{ isComplete?: boolean }|null|undefined} progress
 * @returns {boolean}
 */
export function isWorkflowTaskChecklistBlockingComplete(task, progress = null) {
  const recipe = readWorkflowTaskChecklistRecipe(task);
  if (!recipe.required || recipe.items.length === 0) return false;
  if (!progress || typeof progress !== 'object') return true;
  return progress.isComplete !== true;
}

/**
 * Active people step for Task Checklist auto-expand.
 *
 * @param {object|null|undefined} task
 * @returns {boolean}
 */
export function isWorkflowTaskChecklistActiveStep(task) {
  const status = String(task?.status || '').trim();
  return status === 'ready' || status === 'inProgress';
}
